import React, { type FC, useState, useEffect } from "react";

import { type User, getAuth, onIdTokenChanged, reload } from "firebase/auth";
import type { FormikValues } from "formik";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Auth, useIsMobile } from "senf-atomic-design-system";
import { useModals } from "../../../libraries/senf-atomic-design-system/src/components/molecules/modalStack/ModalProvider";
import { useAuthContext } from "./AuthProvider";
const providerId = {
  google: Math.random().toString(36).substring(2, 15),
  facebook: Math.random().toString(36).substring(2, 15),
  email: Math.random().toString(36).substring(2, 15),
  anonymous: Math.random().toString(36).substring(2, 15),
};

interface AuthModalHeading {
  titleLine1: string;
  titleLine2: string;
  description: string;
}

const AuthModal: FC<{
  success?: (e) => void;
  error?: (e) => void;
  handleClose?: (e) => void;
  authAddDetails?: boolean;
  authVerifyEmail?: boolean;
  authOptions?: boolean;
  reAuth?: boolean;
  loadUserDetails?: () => void;
  project?: any;
  heading?: AuthModalHeading;
}> = (data) => {
  const [page, setPage] = useState("");
  const authContext = useAuthContext();
  const {
    user,
    setUser,
    signIn,
    getUserData,
    createUser,
    loading,
    errorMessage,
    submitEditDetails,
  } = authContext || {};
  const { closeModal, openModal } = useModals();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { t } = useTranslation();
  const {
    success = closeModal,
    error,
    handleClose,
    authAddDetails,
    authVerifyEmail,
    authOptions,
    reAuth,
    loadUserDetails,
    project,
    heading,
    ...props
  } = data;

  useEffect(() => {
    if (authAddDetails) {
      setPage("authAddDetails");
    } else if (authVerifyEmail) {
      setPage("authVerifyEmail");
    } else if (authOptions) {
      console.log("authOptions");
      if (
        window.location.origin === "https://senf.app" ||
        window.location.origin === "https://projects.senf.app"
      ) {
        // trackId
        // THIS TRACKS TWICE tHOUGH
        window?.umami?.("auth_authOpened");
      }
      setPage("authOptions");
    } else if (reAuth) {
      if (
        window.location.origin === "https://senf.app" ||
        window.location.origin === "https://projects.senf.app"
      ) {
        // trackId
        // THIS TRACKS TWICE tHOUGH
        window?.umami?.("auth_reAuthOpened");
      }
      setPage("authOptions");
    } else if (
      window.location.origin === "https://senf.app" ||
      window.location.origin === "https://projects.senf.app"
    ) {
      // trackId
      // THIS TRACKS TWICE tHOUGH
      window?.umami?.("auth_authOpened");
    }
  }, [authAddDetails, authVerifyEmail, authOptions]);

  useEffect(() => {
    //  try to auto login if email has been verified
    // it works only if user didnt leave the current browser when clicking on the email verify link

    // https://firebase.google.com/docs/auth/custom-email-handler
    if (page !== "authVerifyEmail") return;
    const auth = getAuth();
    const unsubscribe = onIdTokenChanged(auth, async (user) => {
      if (user && user.uid && user.emailVerified) {
        const userData = await getUserData(user.uid, project);
        setUser(userData as User);
        setPage("authAddDetails");
      }
      if (user && user.uid && !user.emailVerified) {
        setPage("authVerifyEmail");
      }
      if (!user) {
        setPage("authOptions");
      }
    });

    const interval = setInterval(async () => {
      try {
        if (auth.currentUser) {
          await reload(auth.currentUser);
        }
      } catch (error) {
        throw new Error(
          `error in main.jsx email verification: ${String(error)}`,
        );
      }
    }, 500);
    return () => {
      clearInterval(interval);
      unsubscribe();
    };
  }, [page, getUserData, project, setUser]);

  const [facebookLoading, setFacebookLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [anonymousLoading, setAnonymousLoading] = useState(false);
  const [editLoading, setEditLoading] = useState(false);

  /*   useEffect(() => {
    setPage("authOptions");
  }, []); */

  useEffect(() => {
    const loadingProvider =
      Object.keys(providerId).find((key) => providerId[key] === loading) ||
      loading;
    setFacebookLoading(loadingProvider === "facebook");
    setGoogleLoading(loadingProvider === "google");
    setEmailLoading(loadingProvider === "email");
    setEditLoading(loadingProvider === "edit");
    setAnonymousLoading(loadingProvider === "anonymous");
  }, [loading]);

  const successDefault = (e) => {
    if (!e?.profileCompleted && authAddDetails) {
      try {
        closeModal();
        openModal(<AuthModal authAddDetails={!e?.profileCompleted} />, {
          swipe: !!isMobile,
          size: isMobile ? "fullscreen" : undefined,
          onClose: () => {
            try {
              success?.(e);
            } catch (error) {
              console.error("Error in onClose callback:", error);
              if (window.Sentry) {
                window.Sentry.captureException(error);
              }
            }
          },
          ...props,
        });
      } catch (error) {
        console.error("Error opening/closing modals:", error);
        // Still try to call success callback even if modal operations fail
        try {
          success?.(e);
        } catch (successError) {
          console.error("Error in success callback:", successError);
          if (window.Sentry) {
            window.Sentry.captureException(successError);
          }
        }
      }
      return;
    }
    try {
      success?.(e);
    } catch (error) {
      console.error("Error in success callback:", error);
      if (window.Sentry) {
        window.Sentry.captureException(error);
      }
    }
  };

  const authHandler = {
    ...authContext,
    signIn: {
      facebook: (formikStore: FormikValues) =>
        signIn({
          formikStore,
          provider: "facebook",
          id: providerId.facebook,
        })
          .then(successDefault)
          .catch(error),
      google: (formikStore: FormikValues) =>
        signIn({
          formikStore,
          provider: "google",
          id: providerId.google,
        })
          .then(successDefault)
          .catch(error),
      email: (formikStore: FormikValues) =>
        signIn({
          formikStore,
          provider: "email",
          id: providerId.email,
        })
          .then(successDefault)
          .catch(error),
      anonymous: (formikStore) =>
        signIn({
          formikStore,
          provider: "anonymous",
          id: providerId.anonymous,
        })
          .then(successDefault)
          .catch(error),
    },
    loadingAuth: loading,
    loading: {
      facebook: facebookLoading,
      google: googleLoading,
      email: emailLoading,
      anonymous: anonymousLoading,
    },
    submitEditDetails: (formikStore: FormikValues) =>
      submitEditDetails({ formikStore, project })
        .then((resolvedValue) => {
          success(resolvedValue);
        })
        .catch(error),
    createUser: (formikStore: FormikValues) =>
      createUser(formikStore)
        .then(() => {
          setPage("authVerifyEmail");
          // navigate("/verify");
        })
        .catch(error),
  };
  return (
    <Auth
      errorMessage={t(errorMessage)}
      user={user}
      authHandler={authHandler}
      setPage={setPage}
      page={page}
      reAuth={reAuth}
      handleClose={handleClose}
      loadUserDetails={loadUserDetails}
      project={project}
      heading={heading}
      {...props}
    />
  );
};

export { AuthModal };
